import React, { useMemo, useState } from "react";
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import API_BASE_URL from "../config/api";
import "./styles/SavebackDesk.css";

const sampleCases = [
  {
    caseId: "atlas-budget-save",
    customerName: "Atlas Retention Team",
    segment: "team",
    cancellationReason: "Budget pressure and competitor discount",
    currentOffer: "Growth subscription",
    retentionGoal: "keep annual account active without changing billing provider",
    revenueAtRisk: 1800,
    lastActivityDays: 28,
    openTickets: 1,
  },
  {
    caseId: "northstar-onboarding-save",
    customerName: "Northstar Studio",
    segment: "creator",
    cancellationReason: "Unused seats after onboarding delay",
    currentOffer: "Creator subscription",
    retentionGoal: "recover activation before renewal",
    revenueAtRisk: 420,
    lastActivityDays: 14,
    openTickets: 0,
  },
];

const initialCase = sampleCases[0];

function scoreLocally(caseData) {
  const reason = String(caseData.cancellationReason || "").toLowerCase();
  const segmentWeight = { enterprise: 16, team: 12, creator: 9, solo: 6, trial: 4 }[caseData.segment] || 7;
  const reasonScore = reason.includes("budget") || reason.includes("price")
    ? 26
    : reason.includes("unused")
      ? 16
      : 10;
  const inactivityScore = Math.min(22, Math.max(0, Math.floor(Number(caseData.lastActivityDays || 0) / 2)));
  const supportScore = Math.min(12, Number(caseData.openTickets || 0) * 4);
  const revenueScore = Number(caseData.revenueAtRisk || 0) >= 1000 ? 10 : 6;
  const score = Math.min(100, reasonScore + segmentWeight + inactivityScore + supportScore + revenueScore);
  const riskBand = score >= 72 ? "critical" : score >= 50 ? "elevated" : "watch";
  const discount = score >= 72 ? 25 : score >= 50 ? 15 : 0;

  return {
    ...caseData,
    score,
    riskBand,
    offer: {
      label: discount ? `${discount}% recovery credit for 90 days with an outcome checkpoint` : "guided success checkpoint with no discount",
    },
    messageDraft: `Hi ${caseData.customerName}, I looked at the cancellation reason: ${caseData.cancellationReason}. The best saveback path is tied to ${caseData.retentionGoal}.`,
    followUps: [
      { step: 1, channel: "operator_review", status: "scheduled" },
      { step: 2, channel: "customer_success_check", status: "scheduled" },
      { step: 3, channel: "customer_success_check", status: "scheduled" },
    ],
  };
}

function Metric({ icon, label, value }) {
  return (
    <div className="saveback-metric">
      <span className="saveback-metric-icon">{icon}</span>
      <span className="saveback-metric-label">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SavebackDesk() {
  const [caseData, setCaseData] = useState(initialCase);
  const [preview, setPreview] = useState(scoreLocally(initialCase));
  const [ledger, setLedger] = useState([scoreLocally(initialCase), scoreLocally(sampleCases[1])]);
  const [entitlement, setEntitlement] = useState(localStorage.getItem("savebackEntitlement") || "");
  const [status, setStatus] = useState("Ready");
  const [apiState, setApiState] = useState("local");

  const summary = useMemo(() => {
    const openCases = ledger.filter((item) => item.status !== "recovered").length;
    const average = ledger.length
      ? Math.round(ledger.reduce((sum, item) => sum + Number(item.score || 0), 0) / ledger.length)
      : 0;
    return { openCases, average, total: ledger.length };
  }, [ledger]);

  const updateField = (field, value) => {
    setCaseData((current) => ({
      ...current,
      [field]: field === "revenueAtRisk" || field === "lastActivityDays" || field === "openTickets" ? Number(value) : value,
    }));
  };

  const runPreview = async () => {
    setStatus("Scoring");
    try {
      const response = await fetch(`${API_BASE_URL}/api/saveback/cases/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(caseData),
      });
      if (!response.ok) throw new Error("preview_api_unavailable");
      const body = await response.json();
      setPreview(body.preview);
      setApiState("api");
      setStatus("Offer ready");
    } catch (error) {
      const localPreview = scoreLocally(caseData);
      setPreview(localPreview);
      setApiState("local");
      setStatus("Local preview ready");
    }
  };

  const submitCase = async () => {
    const nextPreview = preview.caseId === caseData.caseId ? preview : scoreLocally(caseData);
    setStatus("Queuing");
    if (entitlement) {
      localStorage.setItem("savebackEntitlement", entitlement);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/saveback/cases`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-saveback-entitlement": entitlement,
        },
        body: JSON.stringify(caseData),
      });
      if (!response.ok) throw new Error("queue_api_unavailable");
      const body = await response.json();
      setLedger((current) => [body.case, ...current.filter((item) => item.caseId !== body.case.caseId)]);
      setApiState("api");
      setStatus(body.queueStatus === "bullmq_jobs_enqueued" ? "Queued in BullMQ" : "Saved");
    } catch (error) {
      setLedger((current) => [{ ...nextPreview, status: "local_preview" }, ...current.filter((item) => item.caseId !== nextPreview.caseId)]);
      setApiState("local");
      setStatus(entitlement ? "Local ledger updated" : "Entitlement needed");
    }
  };

  const loadSample = (index) => {
    setCaseData(sampleCases[index]);
    setPreview(scoreLocally(sampleCases[index]));
    setStatus("Ready");
  };

  return (
    <main className="saveback-desk">
      <section className="saveback-toolbar">
        <div>
          <p className="saveback-kicker">Churn Signal Saveback Desk</p>
          <h1>Saveback Autopilot With Recovery Ledger</h1>
        </div>
        <div className="saveback-toolbar-actions">
          <button type="button" className="saveback-icon-button" title="Load first sample" onClick={() => loadSample(0)}>
            <ReloadOutlined />
          </button>
          <button type="button" className="saveback-icon-button" title="Load second sample" onClick={() => loadSample(1)}>
            <DatabaseOutlined />
          </button>
          <span className={`saveback-runtime saveback-runtime-${apiState}`}>{apiState === "api" ? "API" : "Local"}</span>
        </div>
      </section>

      <section className="saveback-grid">
        <div className="saveback-panel saveback-intake">
          <div className="saveback-panel-header">
            <h2>Case Intake</h2>
            <span>{status}</span>
          </div>
          <label>
            Customer
            <input value={caseData.customerName} onChange={(event) => updateField("customerName", event.target.value)} />
          </label>
          <div className="saveback-two">
            <label>
              Segment
              <select value={caseData.segment} onChange={(event) => updateField("segment", event.target.value)}>
                <option value="enterprise">Enterprise</option>
                <option value="team">Team</option>
                <option value="creator">Creator</option>
                <option value="solo">Solo</option>
                <option value="trial">Trial</option>
              </select>
            </label>
            <label>
              Revenue At Risk
              <input type="number" min="0" value={caseData.revenueAtRisk} onChange={(event) => updateField("revenueAtRisk", event.target.value)} />
            </label>
          </div>
          <label>
            Cancellation Reason
            <textarea value={caseData.cancellationReason} onChange={(event) => updateField("cancellationReason", event.target.value)} />
          </label>
          <label>
            Retention Goal
            <textarea value={caseData.retentionGoal} onChange={(event) => updateField("retentionGoal", event.target.value)} />
          </label>
          <div className="saveback-two">
            <label>
              Last Activity Days
              <input type="number" min="0" value={caseData.lastActivityDays} onChange={(event) => updateField("lastActivityDays", event.target.value)} />
            </label>
            <label>
              Open Tickets
              <input type="number" min="0" value={caseData.openTickets} onChange={(event) => updateField("openTickets", event.target.value)} />
            </label>
          </div>
          <label>
            Manual Entitlement
            <input value={entitlement} onChange={(event) => setEntitlement(event.target.value)} placeholder="saveback-entitlement-..." />
          </label>
          <div className="saveback-actions">
            <button type="button" onClick={runPreview}>
              <ThunderboltOutlined /> Score
            </button>
            <button type="button" onClick={submitCase}>
              <SendOutlined /> Queue
            </button>
          </div>
        </div>

        <div className="saveback-panel saveback-output">
          <div className="saveback-panel-header">
            <h2>Recovery Plan</h2>
            <span className={`saveback-band saveback-band-${preview.riskBand}`}>{preview.riskBand}</span>
          </div>
          <div className="saveback-score-row">
            <Metric icon={<AlertOutlined />} label="Churn Score" value={preview.score} />
            <Metric icon={<ClockCircleOutlined />} label="Follow Ups" value={preview.followUps?.length || 0} />
            <Metric icon={<CheckCircleOutlined />} label="Open Cases" value={summary.openCases} />
          </div>
          <div className="saveback-offer">
            <h3>{preview.offer?.label}</h3>
            <p>{preview.messageDraft}</p>
          </div>
          <div className="saveback-followups">
            {(preview.followUps || []).map((item) => (
              <div key={`${preview.caseId}-${item.step}`} className="saveback-followup">
                <span>{item.step}</span>
                <strong>{item.channel}</strong>
                <em>{item.status}</em>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="saveback-ledger">
        <div className="saveback-panel-header">
          <h2>Recovery Ledger</h2>
          <span>{summary.total} cases, average score {summary.average}</span>
        </div>
        <div className="saveback-ledger-table">
          {ledger.map((item) => (
            <div className="saveback-ledger-row" key={item.caseId}>
              <strong>{item.customerName}</strong>
              <span>{item.riskBand}</span>
              <span>{item.score}</span>
              <span>{item.offer?.label || "pending"}</span>
              <em>{item.status || "offer_ready"}</em>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

export default SavebackDesk;
